import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Publicite.css';
import { FiSearch, FiEye, FiTrash2, FiDownload, FiPause, FiX, FiCheck, FiPlay } from 'react-icons/fi';
import { FaSort } from 'react-icons/fa';
import { usePublicite } from '../hooks/usePublicite';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import RejectModal from '../components/modals/RejectModal';
import SuspendModal from '../components/modals/SuspendModal';

export default function Publicite() {
  const navigate = useNavigate();
  const { publicites: data, loading, error, updatePubliciteStatus, deletePublicite } = usePublicite();
  const [search, setSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  

  const getProviderIcon = (index) => {
    // Icônes exactes du Figma
    const icons = [
      <div style={{ backgroundColor: '#3B82F6', color: 'white', width: '24px', height: '24px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>📊</div>,
      <div style={{ backgroundColor: '#F59E0B', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>🟠</div>,
      <div style={{ backgroundColor: '#6B7280', color: 'white', width: '24px', height: '24px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>⚪</div>,
      <div style={{ backgroundColor: '#8B5CF6', color: 'white', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>🟣</div>
    ];
    
    return icons[index % 4];
  };

  // Fonctions supprimées - utilisation directe des données de l'API
  // Les données proviennent maintenant directement de publicites via usePublicite()

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette campagne ?')) {
      await deletePublicite(id);
    }
  };

  const handleSelectItem = (id) => {
    // Ensure we're comparing the same types (strings)
    const idStr = String(id);
    setSelectedItems(prev => 
      prev.includes(idStr) 
        ? prev.filter(item => item !== idStr)
        : [...prev, idStr]
    );
  };

  const handleViewDetails = (id) => {
    navigate(`/publicite/${id}`);
  };

  const handleReject = async (campaign) => {
    const reason = window.prompt(`Motif de rejet pour la publicité de ${campaign.business_name}:`);
    if (reason && reason.trim()) {
      await updatePubliciteStatus(campaign.id, 'rejeté', reason);
    }
  };

  const handleSuspend = async (campaign) => {
    const reason = window.prompt(`Motif de suspension pour la publicité de ${campaign.business_name}:`);
    if (reason && reason.trim()) {
      await updatePubliciteStatus(campaign.id, 'suspendu', reason);
    }
  };

  const handleAccept = async (id, businessName) => {
    const confirm = window.confirm(`Voulez-vous activer la publicité de ${businessName} ?`);
    if (confirm) {
      await updatePubliciteStatus(id, 'accepté');
    }
  };

  const handleReactivate = async (id, businessName) => {
    const confirm = window.confirm(`Voulez-vous réactiver la publicité de ${businessName} ?`);
    if (confirm) {
      await updatePubliciteStatus(id, 'en_cours');
    }
  };


  const confirmReject = async (reason) => {
    if (currentCampaign) {
      await updatePubliciteStatus(currentCampaign.id, 'rejeté', reason);
      setShowRejectModal(false);
      setCurrentCampaign(null);
    }
  };

  const confirmSuspend = async (reason) => {
    if (currentCampaign) {
      await updatePubliciteStatus(currentCampaign.id, 'suspendu', reason);
      setShowSuspendModal(false);
      setCurrentCampaign(null);
    }
  };

  const exportCSV = () => {
    const headers = ['ID', 'Prestataire', 'De', 'À', 'Emplacement', 'Prix', 'Statut'];
    const rows = data.map((d) => [
      d.id,
      d.business_name,
      d.start_date,
      d.end_date,
      d.emplacement || '',
      `${d.cost_per_view} FCFA`,
      d.status,
    ]);
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers, ...rows].map((e) => e.join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'publicites.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status) => {
    // Mapping des statuts réels de la base de données (français)
    const statusConfig = {
      'active': { label: 'En cours', class: 'status-active' },
      'en_cours': { label: 'En cours', class: 'status-active' },
      'draft': { label: 'En attente', class: 'status-pending' },
      'en_attente': { label: 'En attente', class: 'status-pending' },
      'paused': { label: 'Suspendu', class: 'status-suspended' },
      'suspendu': { label: 'Suspendu', class: 'status-suspended' },
      'rejected': { label: 'Rejeté', class: 'status-rejected' },
      'rejeté': { label: 'Rejeté', class: 'status-rejected' },
      'completed': { label: 'Terminé', class: 'status-completed' },
      'terminé': { label: 'Terminé', class: 'status-completed' },
      'accepté': { label: 'Accepté', class: 'status-active' }
    };
    
    const config = statusConfig[status] || { label: status, class: 'status-default' };
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const getActionButton = (index, campaignData) => {
    // Actions basées sur le statut réel de la campagne (support français et anglais)
    const status = campaignData.status;
    const campaignId = campaignData.id;
    const businessName = campaignData.business_name;
    
    switch (status) {
      case 'active':
      case 'en_cours':
      case 'accepté':
        return (
          <button 
            className="action-btn suspend"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSuspend(campaignData);
            }}
            title="Suspendre"
          >
            <FiPause className="icon suspend-icon" />
          </button>
        );
      
      case 'draft':
      case 'en_attente':
        return (
          <div className="action-buttons">
            <button 
              className="action-btn reject"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleReject(campaignData);
              }}
              title="Rejeter"
            >
              <FiX className="icon reject-icon" />
            </button>
            <button 
              className="action-btn accept"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAccept(campaignId, businessName);
              }}
              title="Accepter"
            >
              <FiCheck className="icon accept-icon" />
            </button>
          </div>
        );
      
      case 'paused':
      case 'suspendu':
        return (
          <button 
            className="action-btn reactivate"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleReactivate(campaignId, businessName);
            }}
            title="Réactiver"
          >
            <FiPlay className="icon reactivate-icon" />
          </button>
        );
      
      case 'rejected':
      case 'rejeté':
        return (
          <div className="action-buttons">
            <button 
              className="action-btn reject"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleReject(campaignData);
              }}
              title="Rejeter"
            >
              <FiX className="icon reject-icon" />
            </button>
            <button 
              className="action-btn accept"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAccept(campaignId, businessName);
              }}
              title="Accepter"
            >
              <FiCheck className="icon accept-icon" />
            </button>
          </div>
        );
      
      case 'completed':
      case 'terminé':
        return (
          <button 
            className="action-btn reactivate"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleReactivate(campaignId, businessName);
            }}
            title="Réactiver"
          >
            <FiPlay className="icon reactivate-icon" />
          </button>
        );
      
      default:
        return null;
    }
  };

  // Fonctions de tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <FaSort className="sort-icon" />;
    }
    return sortDirection === 'asc' 
      ? <FaSort className="sort-icon active" style={{ color: '#2563eb' }} />
      : <FaSort className="sort-icon active" style={{ color: '#2563eb', transform: 'rotate(180deg)' }} />;
  };

  const sortData = (data) => {
    if (!sortField) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Gestion spéciale pour les champs
      switch (sortField) {
        case 'business_name':
          aValue = aValue || '';
          bValue = bValue || '';
          break;
        case 'start_date':
        case 'end_date':
          // Conversion des dates DD/MM/YYYY vers Date pour comparaison
          if (aValue && aValue.includes('/')) {
            const [day, month, year] = aValue.split('/');
            aValue = new Date(year, month - 1, day);
          }
          if (bValue && bValue.includes('/')) {
            const [day, month, year] = bValue.split('/');
            bValue = new Date(year, month - 1, day);
          }
          break;
        case 'cost_per_view':
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
          break;
        default:
          aValue = aValue || '';
          bValue = bValue || '';
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filtrage et tri des données
  const filtered = Array.isArray(data)
    ? data.filter((d) =>
        JSON.stringify(d || {}).toLowerCase().includes(search.toLowerCase())
      )
    : [];

  const sortedAndFiltered = sortData(filtered);

  // Pagination
  const pagination = usePagination(sortedAndFiltered, 'publicite');

  const handleSelectAll = () => {
    if (selectedItems.length === pagination.paginatedData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(pagination.paginatedData.map(item => String(item.id)));
    }
  };

  // Reset pagination when search changes
  useEffect(() => {
    pagination.resetPagination();
  }, [search]);

  return (
    <div className="publicite-container">
      <div className="publicite-header">
        <h1>Publicité & Visibilité</h1>
        <button className="export-btn" onClick={exportCSV}>
          <FiDownload />
          Export CSV
        </button>
      </div>

      <div className="search-container">
        <div className="search-wrapper">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Recherche nom, email, tel ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-bar"
          />
        </div>
      </div>

      <div className="table-container">
        <table className="publicite-table">
          <thead>
            <tr>
              <th className="checkbox-column">
                <input
                  type="checkbox"
                  checked={selectedItems.length === pagination.paginatedData.length && pagination.paginatedData.length > 0}
                  onChange={handleSelectAll}
                />
                <span className="th-id">Id</span>
              </th>
              <th className="sortable" onClick={() => handleSort('business_name')}>
                <div className="th-content">
                  {getSortIcon('business_name')}
                  <span>Prestataire</span>
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('start_date')}>
                <div className="th-content">
                  {getSortIcon('start_date')}
                  <span>De</span>
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('end_date')}>
                <div className="th-content">
                  {getSortIcon('end_date')}
                  <span>À</span>
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('emplacement')}>
                <div className="th-content">
                  {getSortIcon('emplacement')}
                  <span>Emplacement</span>
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('cost_per_view')}>
                <div className="th-content">
                  {getSortIcon('cost_per_view')}
                  <span>Prix</span>
                </div>
              </th>
              <th className="sortable" onClick={() => handleSort('status')}>
                <div className="th-content">
                  {getSortIcon('status')}
                  <span>Statut</span>
                </div>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="9" className="loading">Chargement...</td>
            </tr>
          ) : error ? (
            <tr>
              <td colSpan="9" className="error">{error}</td>
            </tr>
          ) : pagination.paginatedData.length === 0 ? (
            <tr>
              <td colSpan="9" className="no-data">Aucune publicité trouvée.</td>
            </tr>
          ) : (
            pagination.paginatedData.map((ligne, index) => {
              // Capturer les valeurs dans des variables locales pour éviter les problèmes de closure
              const campaignId = String(ligne.id);
              const campaignData = {
                id: campaignId,
                business_name: ligne.business_name,
                status: ligne.status,
                start_date: ligne.start_date,
                end_date: ligne.end_date,
                emplacement: ligne.emplacement,
                cost_per_view: ligne.cost_per_view
              };
              
              return (
              <tr key={campaignId}>
                <td className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(campaignId)}
                    onChange={() => handleSelectItem(campaignId)}
                  />
                  <span className="client-id">{campaignId.substring(0, 8)}</span>
                </td>
                <td>
                  <div className="prestataire-info">
                    <div className="prestataire-icon">
                      {getProviderIcon(index)}
                    </div>
                    <span className="prestataire-name">{campaignData.business_name}</span>
                  </div>
                </td>
                <td>{campaignData.start_date}</td>
                <td>{campaignData.end_date}</td>
                <td>{campaignData.emplacement}</td>
                <td>{campaignData.cost_per_view} FCFA</td>
                <td>{getStatusBadge(campaignData.status)}</td>
                <td>
                  <div className="table-actions">
                    {getActionButton(index, campaignData)}
                    <div className="action-icons">
                      <FiEye 
                        className="icon view-icon" 
                        onClick={() => handleViewDetails(campaignId)}
                      />
                      <FiTrash2 
                        className="icon delete-icon" 
                        onClick={() => handleDelete(campaignId)}
                      />
                    </div>
                  </div>
                </td>
              </tr>
              );
            })
          )}
        </tbody>
        </table>
        
        {/* Pagination */}
        <Pagination
          totalItems={pagination.totalItems}
          totalPages={pagination.totalPages}
          currentPage={pagination.currentPage}
          pageSize={pagination.pageSize}
          startIndex={pagination.startIndex}
          endIndex={pagination.endIndex}
          hasNextPage={pagination.hasNextPage}
          hasPreviousPage={pagination.hasPreviousPage}
          isFirstPage={pagination.isFirstPage}
          isLastPage={pagination.isLastPage}
          onPageChange={pagination.goToPage}
          onPageSizeChange={pagination.changePageSize}
          config={pagination.config}
        />
      </div>

      {showRejectModal && (
        <RejectModal
          isOpen={showRejectModal}
          onClose={() => setShowRejectModal(false)}
          onConfirm={confirmReject}
        />
      )}

      {showSuspendModal && (
        <SuspendModal
          isOpen={showSuspendModal}
          onClose={() => setShowSuspendModal(false)}
          onConfirm={confirmSuspend}
        />
      )}
    </div>
  );
}
